import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { badgeClass, formatNumber, getApiErrorMessage, inputClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Priority = 'urgent' | 'high' | 'medium' | 'low';

interface Step {
  id: string;
  sequence: number;
  name: string;
  status: string;
  workCenterName: string | null;
  isOutsourced: boolean;
  remainingMinutes: number;
  start: string | null;
  end: string | null;
}
interface OrderRow {
  workOrderId: number;
  workOrderNumber: string;
  productName: string | null;
  sku: string | null;
  unit: string | null;
  plannedQuantity: number;
  status: string;
  priority: Priority;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  lateByDays: number | null;
  late: boolean;
  remainingMinutes: number;
  notes: string[];
  operations: Step[];
}
interface Job {
  workOrderId: number;
  workOrderNumber: string;
  productName: string | null;
  priority: Priority;
  operationId: string;
  sequence: number;
  operation: string;
  remainingMinutes: number;
  start: string | null;
  end: string | null;
  dueDate: string | null;
}
interface CenterRow {
  workCenterId: string;
  code: string;
  name: string;
  capacity: number;
  hoursPerDay: number;
  jobs: number;
  loadMinutes: number;
  availableMinutes: number;
  requiredMinutes: number;
  requiredPercent: number | null;
  overloaded: boolean;
  busyUntil: string | null;
  days: { date: string; available: number; scheduled: number }[];
  queue: Job[];
}
interface CapacityPlan {
  today: string;
  days: number;
  windowEnd: string;
  summary: { workOrders: number; late: number; overloadedWorkCenters: number; withoutRouting: number; onHold: number; remainingMinutes: number };
  workCenters: CenterRow[];
  data: OrderRow[];
  total: number;
  totalPages: number;
}

const PAGE_SIZES = [10, 25, 50, 100];
const WINDOWS = [7, 14, 28, 56, 84];
const PRIORITIES: Priority[] = ['urgent', 'high', 'medium', 'low'];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const PRIORITY_CLASS: Record<Priority, string> = {
  urgent: 'bg-red-50 text-red-700 ring-red-600/20',
  high: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  medium: 'bg-gray-50 text-gray-700 ring-gray-500/20',
  low: 'bg-gray-50 text-gray-500 ring-gray-500/20',
};
const capitalise = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).replace('_', ' ');
const shortDate = (value?: string | null) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—');
const span = (start: string | null, end: string | null, today: string) => {
  if (!start || !end) return '—';
  const name = (day: string) => (day === today ? 'Today' : shortDate(day));
  return start === end ? name(start) : `${name(start)} – ${name(end)}`;
};
const hours = (minutes: number) => {
  const whole = Math.round(minutes);
  if (whole < 60) return `${whole} min`;
  return `${formatNumber(Math.floor(whole / 60))} h${whole % 60 ? ` ${whole % 60} min` : ''}`;
};
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== false));
// How full a day is, as a shade
const shade = (available: number, scheduled: number) => {
  if (available <= 0) return 'bg-gray-100';
  const share = scheduled / available;
  if (share <= 0.005) return 'bg-white ring-1 ring-inset ring-gray-200';
  if (share < 0.5) return 'bg-indigo-200';
  if (share < 0.95) return 'bg-indigo-400';
  return 'bg-indigo-600';
};

const capacityApi = {
  plan: async (params: object): Promise<CapacityPlan> => (await api.get('/manufacturing/capacity', { params: clean(params) })).data,
  reschedule: async (id: number, data: object) => (await api.post(`/manufacturing/capacity/work-orders/${id}/reschedule`, data)).data,
  setPriority: async (id: number, priority: Priority) => (await api.post(`/manufacturing/capacity/work-orders/${id}/priority`, { priority })).data,
};

const Capacity: React.FC = () => {
  const { can } = usePermissions();
  const canEdit = can('manufacturing.update');
  const queryClient = useQueryClient();

  const [days, setDays] = useState(28);
  const [workCenterId, setWorkCenterId] = useState('');
  const [lateOnly, setLateOnly] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(Math.max(25, defaultRowsPerPage()));
  const [queueOf, setQueueOf] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['capacity', { days, workCenterId, lateOnly, search, page, limit }],
    queryFn: () => capacityApi.plan({ days, workCenterId, lateOnly, search, page, limit }),
    placeholderData: keepPreviousData,
  });
  // The list of work centers to choose from does not shrink when one is chosen
  const { data: everything } = useQuery({ queryKey: ['capacity', 'centers'], queryFn: () => capacityApi.plan({ days: 7, limit: 1 }) });

  const rerun = () => queryClient.invalidateQueries({ queryKey: ['capacity'] });
  const move = useMutation({
    mutationFn: (order: OrderRow) => capacityApi.reschedule(order.workOrderId, { plannedStartDate: order.scheduledStart, plannedEndDate: order.scheduledEnd }),
    onSuccess: (saved) => {
      toast.success(`${saved.workOrderNumber} now planned to finish on ${shortDate(saved.plannedEndDate)}`);
      rerun();
    },
    onError: (moveError) => toast.error(getApiErrorMessage(moveError, 'The dates could not be moved')),
  });
  const prioritise = useMutation({
    mutationFn: ({ order, priority }: { order: OrderRow; priority: Priority }) => capacityApi.setPriority(order.workOrderId, priority),
    onSuccess: (saved) => {
      toast.success(`${saved.workOrderNumber} is now ${saved.priority} priority; the schedule was worked out again`);
      rerun();
    },
    onError: (priorityError) => toast.error(getApiErrorMessage(priorityError, 'The priority could not be changed')),
  });

  const today = data?.today ?? '';
  const centers = data?.workCenters ?? [];
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const queueCenter = centers.find((center) => center.workCenterId === queueOf) ?? null;
  const order = rows.find((row) => row.workOrderId === orderId) ?? null;
  const summary = data?.summary;
  const stats = [
    { label: 'Open work orders', value: formatNumber(summary?.workOrders ?? 0), hint: `${hours(summary?.remainingMinutes ?? 0)} of work still to do` },
    { label: 'Late by the schedule', value: formatNumber(summary?.late ?? 0), hint: 'Cannot finish by their planned end date', alert: (summary?.late ?? 0) > 0 },
    { label: 'Overloaded work centers', value: formatNumber(summary?.overloadedWorkCenters ?? 0), hint: `Asked for more than they have in ${days} days`, alert: (summary?.overloadedWorkCenters ?? 0) > 0 },
    { label: 'Not scheduled', value: formatNumber((summary?.withoutRouting ?? 0) + (summary?.onHold ?? 0)), hint: `${formatNumber(summary?.withoutRouting ?? 0)} without a routing · ${formatNumber(summary?.onHold ?? 0)} on hold` },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Capacity &amp; Schedule</h1>
          <p className="mt-1 text-sm text-gray-500">The open work orders laid out on the work centers within their hours, the most urgent first: how full each day is, what each work center runs next, and when each work order can really finish.</p>
        </div>
        <button type="button" onClick={() => refetch()} disabled={isFetching} className={primaryButtonClass}>
          <ArrowPathIcon className={cn('-ml-0.5 mr-2 h-4 w-4', isFetching && 'animate-spin')} />
          Schedule Again
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.alert ? 'text-red-600' : 'text-gray-900')}>{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      {/* ---- work centers */}
      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-gray-200 p-4 text-sm text-gray-700">
          <h2 className="text-sm font-semibold text-gray-900">Work centers</h2>
          <label className="flex items-center gap-2">
            Show the next
            <select value={days} onChange={(event) => setDays(Number(event.target.value))} aria-label="Window" className={cn(inputClass, 'w-28 py-1.5')}>
              {WINDOWS.map((count) => (<option key={count} value={count}>{count} days</option>))}
            </select>
          </label>
          <select value={workCenterId} onChange={(event) => { setWorkCenterId(event.target.value); setPage(1); }} aria-label="Work center" className={cn(inputClass, 'w-56 py-1.5')}>
            <option value="">All work centers</option>
            {(everything?.workCenters ?? []).map((center) => (<option key={center.workCenterId} value={center.workCenterId}>{center.name}</option>))}
          </select>
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="inline-block h-3 w-3 rounded-sm bg-white ring-1 ring-inset ring-gray-200" /> free
            <span className="ml-2 inline-block h-3 w-3 rounded-sm bg-indigo-200" /> part
            <span className="ml-2 inline-block h-3 w-3 rounded-sm bg-indigo-600" /> full
            <span className="ml-2 inline-block h-3 w-3 rounded-sm bg-gray-100" /> day off
          </span>
        </div>
        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Work center</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Work waiting</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Asked for by due dates</th>
                <th scope="col" className={thClass}>Busy until</th>
                <th scope="col" className={thClass}>Day by day, as scheduled</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The schedule could not be worked out.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">Scheduling…</td></tr>
              ) : centers.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">No work centers yet. Add them under Master Data, and put them on the routings.</td></tr>
              ) : (
                centers.map((center) => (
                  <tr key={center.workCenterId}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{center.name}</p>
                      <p className="text-xs text-gray-500">{center.code} · {formatNumber(center.hoursPerDay)} h a day{center.capacity > 1 ? ` × ${center.capacity} machines` : ''}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {hours(center.loadMinutes)}
                      <span className="block text-xs text-gray-500">{center.jobs} step(s)</span>
                    </td>
                    <td className={cn('whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums', center.overloaded ? 'font-semibold text-red-600' : 'text-gray-700')}>
                      {center.requiredPercent === null ? 'No hours' : `${formatNumber(center.requiredPercent)}%`}
                      <span className="block text-xs font-normal text-gray-500">{hours(center.requiredMinutes)} of {hours(center.availableMinutes)}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{center.busyUntil ? (center.busyUntil === today ? 'Today' : shortDate(center.busyUntil)) : 'Free'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-0.5" role="img" aria-label={`Load of ${center.name} by day`}>
                        {center.days.map((day) => (
                          <span key={day.date} title={`${shortDate(day.date)}: ${day.available > 0 ? `${hours(day.scheduled)} of ${hours(day.available)}` : 'day off'}`} className={cn('h-5 w-3 rounded-sm', shade(day.available, day.scheduled))} />
                        ))}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setQueueOf(center.workCenterId)} disabled={center.jobs === 0} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50 disabled:opacity-40" aria-label={`Queue of ${center.name}`}>
                        Queue
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- work orders */}
      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <h2 className="text-sm font-semibold text-gray-900">Work orders, in the order they are scheduled</h2>
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by work order or product" aria-label="Search work orders" className={cn(inputClass, 'pl-9')} />
          </div>
          <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-700">
            <input type="checkbox" checked={lateOnly} onChange={(event) => { setLateOnly(event.target.checked); setPage(1); }} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            Only what is late
          </label>
        </div>
        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Work order</th>
                <th scope="col" className={thClass}>Priority</th>
                <th scope="col" className={thClass}>Planned</th>
                <th scope="col" className={thClass}>Scheduled</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Work left</th>
                <th scope="col" className={thClass}>Outcome</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Scheduling…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{search || lateOnly || workCenterId ? 'No work order matches' : 'No open work orders'}</p>
                    <p className="mt-1 text-sm text-gray-500">Draft, planned and running work orders are scheduled here.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.workOrderId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setOrderId(row.workOrderId)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.workOrderNumber}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{row.productName ?? '—'} × {formatNumber(row.plannedQuantity)} · {capitalise(row.status)}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {canEdit ? (
                        <select value={row.priority} onChange={(event) => prioritise.mutate({ order: row, priority: event.target.value as Priority })} disabled={prioritise.isPending} aria-label={`Priority of ${row.workOrderNumber}`} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                          {PRIORITIES.map((priority) => (<option key={priority} value={priority}>{capitalise(priority)}</option>))}
                        </select>
                      ) : (
                        <span className={cn(badgeClass, PRIORITY_CLASS[row.priority] ?? PRIORITY_CLASS.medium)}>{capitalise(row.priority)}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{span(row.plannedStartDate, row.plannedEndDate, today)}</td>
                    <td className={cn('whitespace-nowrap px-4 py-3 text-sm', row.late ? 'font-medium text-red-600' : 'text-gray-900')}>{span(row.scheduledStart, row.scheduledEnd, today)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{hours(row.remainingMinutes)}</td>
                    <td className="px-4 py-3 text-sm">
                      {row.late ? (
                        <span className="font-medium text-red-600">{row.lateByDays === null ? 'Does not fit' : `${row.lateByDays} day(s) late`}</span>
                      ) : row.scheduledEnd ? (
                        <span className="text-green-700">On time</span>
                      ) : (
                        <span className="text-gray-500">Not scheduled</span>
                      )}
                      {row.notes.map((note) => (<span key={note} className="block text-xs text-gray-500">{note}</span>))}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {canEdit && row.late && row.lateByDays !== null && row.scheduledStart && (
                        <button type="button" onClick={() => move.mutate(row)} disabled={move.isPending} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Use the scheduled dates for ${row.workOrderNumber}`} title="Sets the planned dates of the work order to what the schedule says">
                          Use Scheduled Dates
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Work orders are taken by priority, then by planned end date. A step takes its setup time and its run time for the pieces still to make, on the days its work center works (Monday to Saturday when none are set), and never more on a day than the work center has hours.
        A work order not yet started waits for its planned start date. Steps done outside and steps without a work center take no time here.
      </p>

      {/* ---- the queue of one work center */}
      <Modal isOpen={!!queueCenter} onClose={() => setQueueOf(null)} title={queueCenter ? `${queueCenter.name}: what runs next` : 'Queue'} size="xl">
        {queueCenter && (
          <div className="space-y-4">
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className={thClass}>#</th>
                    <th scope="col" className={thClass}>Work order</th>
                    <th scope="col" className={thClass}>Step</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>Time</th>
                    <th scope="col" className={thClass}>When</th>
                    <th scope="col" className={thClass}>Order due</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {queueCenter.queue.map((job, index) => (
                    <tr key={job.operationId}>
                      <td className="px-4 py-2 text-sm text-gray-500">{index + 1}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {job.workOrderNumber} <span className={cn(badgeClass, 'ml-1', PRIORITY_CLASS[job.priority] ?? PRIORITY_CLASS.medium)}>{capitalise(job.priority)}</span>
                        <span className="block text-xs text-gray-500">{job.productName ?? '—'}</span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">{job.sequence} · {job.operation}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-700 tabular-nums">{hours(job.remainingMinutes)}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">{span(job.start, job.end, today)}</td>
                      <td className={cn('whitespace-nowrap px-4 py-2 text-sm', job.dueDate && job.end && job.end > job.dueDate ? 'font-medium text-red-600' : 'text-gray-700')}>{shortDate(job.dueDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={() => setQueueOf(null)} className={secondaryButtonClass}>Close</button>
            </div>
          </div>
        )}
      </Modal>

      {/* ---- the steps of one work order */}
      <Modal isOpen={!!order} onClose={() => setOrderId(null)} title={order ? `${order.workOrderNumber}: step by step` : 'Work order'} size="xl">
        {order && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-700">
              <span className="font-semibold text-gray-900">{order.productName ?? '—'} × {formatNumber(order.plannedQuantity)} {order.unit ?? ''}</span>
              <span className={cn(badgeClass, PRIORITY_CLASS[order.priority] ?? PRIORITY_CLASS.medium)}>{capitalise(order.priority)}</span>
              <span>Planned {span(order.plannedStartDate, order.plannedEndDate, today)}</span>
              <span className={order.late ? 'font-medium text-red-600' : ''}>Scheduled {span(order.scheduledStart, order.scheduledEnd, today)}</span>
            </div>
            {order.operations.length === 0 ? (
              <p className="text-sm text-gray-500">This work order has no routing, so there is nothing to schedule. Give the product a routing and load it onto the work order.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className={thClass}>Step</th>
                      <th scope="col" className={thClass}>Work center</th>
                      <th scope="col" className={thClass}>State</th>
                      <th scope="col" className={cn(thClass, 'text-right')}>Time left</th>
                      <th scope="col" className={thClass}>When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {order.operations.map((step) => (
                      <tr key={step.id}>
                        <td className="px-4 py-2 text-sm text-gray-900">{step.sequence} · {step.name}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{step.isOutsourced ? 'Outside job work' : step.workCenterName ?? 'None'}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{capitalise(step.status)}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-700 tabular-nums">{step.status === 'completed' ? '—' : hours(step.remainingMinutes)}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">{step.status === 'completed' ? 'Done' : span(step.start, step.end, today)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {order.notes.length > 0 && <p className="text-xs text-gray-500">{order.notes.join(' · ')}</p>}
            <div className="flex justify-end">
              <button type="button" onClick={() => setOrderId(null)} className={secondaryButtonClass}>Close</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Capacity;
